import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-95",
  {
    variants: {
      variant: {
        default: "relative bg-gradient-to-r from-blue-600/90 to-indigo-600/90 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:-translate-y-0.5 border border-white/20 backdrop-blur-md before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-b before:from-white/10 before:to-transparent before:pointer-events-none",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-md",
        outline: "border-2 border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50/50 hover:text-blue-700 hover:shadow-md hover:-translate-y-0.5",
        secondary: "bg-slate-100 text-slate-900 hover:bg-slate-200 hover:shadow-sm",
        ghost: "hover:bg-blue-50 hover:text-blue-700",
        link: "text-blue-600 underline-offset-4 hover:underline",
        // POS-specific variants
        hero: "bg-gradient-primary text-primary-foreground hover:shadow-glow hover:scale-[1.02] font-semibold",
        success: "bg-success text-success-foreground hover:bg-success/90 hover:shadow-md",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90 hover:shadow-md",
        pos: "bg-card text-card-foreground border border-border hover:bg-accent hover:text-accent-foreground hover:shadow-md",
        kitchen: "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:shadow-glow font-medium",
        payment: "bg-gradient-primary text-primary-foreground hover:shadow-glow hover:scale-[1.02] font-semibold animate-pulse-glow",
      },
      size: {
        default: "h-10 px-5 py-2 rounded-full",
        sm: "h-9 rounded-full px-4",
        lg: "h-12 rounded-full px-8 text-base",
        xl: "h-14 rounded-full px-10 text-lg",
        icon: "h-10 w-10 rounded-full",
        "icon-lg": "h-12 w-12 rounded-full",
        pos: "h-16 px-6 py-4 text-base rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"
// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
